import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstTable from "../../components/DstTable/DstTable";
import MultiFilterDropdown from "../../components/MultiFilterDropdown/MultiFilterDropdown";
import { useSession } from "../../contexts/SessionContext";
import { computeDay, formatMinutesHourly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { fetchMeetingTypes, fetchMeetingsWorkload, generateRemoteUrl } from "./MeetingsWorkload.function";

const MeetingsWorkload = () => {
  const { language } = useSession();
  const [workload, setWorkload] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [meetingTypes, setMeetingTypes] = useState<any[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [selectedTypeIds, setSelectedTypeIds] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<string>(computeDay());
  const [endDate, setEndDate] = useState<string>(computeDay(7));
  const [tempStartDate, setTempStartDate] = useState<string>(computeDay());
  const [tempEndDate, setTempEndDate] = useState<string>(computeDay(7));
  const [dateError, setDateError] = useState(false);

  const remoteUrl = useMemo(
    () => generateRemoteUrl(startDate, endDate, selectedTypeIds),
    [startDate, endDate, selectedTypeIds]
  );

  useEffect(() => {
    fetchMeetingTypes(setMeetingTypes, setTypesLoading, language);
  }, [language]);

  useEffect(() => {
    fetchMeetingsWorkload(remoteUrl, language, setWorkload, setLoading);
  }, [remoteUrl, language]);

  const columns = [
    {
      key: "index",
      name: "#",
    },
    {
      key: "user.full_name",
      name: translate(language, "GLOBAL.NAME"),
      render: (item: any) => (
        <Link to={`/user/${item.user.id}`} className="text-decoration-underline">
          {item.user.full_name}
        </Link>
      ),
    },
    {
      key: "user.email",
      name: translate(language, "GLOBAL.EMAIL"),
    },
    {
      key: "count",
      name: translate(language, "PAGES.MEETINGS_WORKLOAD.COLUMNS.COUNT"),
    },
    {
      key: "load",
      name: translate(language, "PAGES.MEETINGS_WORKLOAD.COLUMNS.LOAD"),
      render: (item: any) => formatMinutesHourly(item.load),
    },
  ];

  return (
    <div className="p-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <h1 className="mb-0">{translate(language, "PAGES.MEETINGS_WORKLOAD.TITLE")}</h1>
        <div className="d-flex flex-wrap justify-content-end align-items-end gap-3 ms-auto">
          <div className="d-flex flex-wrap align-items-center gap-2">
            <div className="w-fit mt-1">
              <MultiFilterDropdown
                inputTitle={translate(language, "PAGES.MEETINGS_WORKLOAD.FILTERS.MEETING_TYPE")}
                tags={meetingTypes.map((meetingType: any) => ({
                  value: meetingType.id,
                  display: meetingType.name,
                }))}
                onSelectTags={(tags) => setSelectedTypeIds(tags.map(String))}
                defaultValue={selectedTypeIds}
                buttonWidth="120px"
                showSearchBar
                language={language}
                mock={typesLoading}
              />
            </div>
            {selectedTypeIds.length > 0 && (
              <div
                className="ms-1 clickable dark-gray-font text-decoration-underline xs-regular w-fit align-content-center"
                onClick={() => setSelectedTypeIds([])}
              >
                {translate(language, "PAGES.MEETINGS_WORKLOAD.FILTERS.CLEAR_FILTERS")}
              </div>
            )}
          </div>
          <div className="d-flex align-items-end">
            <div className="w-fit me-2">
              <label htmlFor="start-date" className="form-label dark-gray-font xs-regular mb-1">
                {translate(language, "GLOBAL.START_DATE")}
              </label>
              <input
                type="date"
                id="start-date"
                className={`form-control s-regular ${dateError ? "is-invalid" : ""}`}
                value={tempStartDate}
                onChange={(event) => setTempStartDate(event.target.value)}
              />
            </div>
            <div className="w-fit me-2">
              <label htmlFor="end-date" className="form-label dark-gray-font xs-regular mb-1">
                {translate(language, "GLOBAL.END_DATE")}
              </label>
              <input
                type="date"
                id="end-date"
                className={`form-control s-regular ${dateError ? "is-invalid" : ""}`}
                value={tempEndDate}
                onChange={(event) => setTempEndDate(event.target.value)}
              />
            </div>
            <DstButton
              clickFunction={() => {
                if (tempStartDate && tempEndDate && new Date(tempEndDate) < new Date(tempStartDate)) {
                  setDateError(true);
                  return;
                }
                setDateError(false);
                setStartDate(tempStartDate);
                setEndDate(tempEndDate);
              }}
              value=""
              btnClass="py-2"
              btnImageBefore={<span className="dst-icon-search-md" />}
              tooltip={translate(language, "PAGES.MEETINGS_WORKLOAD.BUTTONS.APPLY_DATES")}
            />
            <DstButton
              clickFunction={() => {
                setTempStartDate("");
                setTempEndDate("");
                setStartDate("");
                setEndDate("");
              }}
              value=""
              btnClass="py-2 ms-2"
              btnImageBefore={<span className="dst-icon-eraser" />}
              tooltip={translate(language, "PAGES.MEETINGS_WORKLOAD.BUTTONS.CLEAR_DATES")}
              variant="secondary"
            />
          </div>
        </div>
      </div>

      <DstTable columns={columns} data={workload} loading={loading} striped />
    </div>
  );
};

export default MeetingsWorkload;
